import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Select,
} from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import { exportExpensesToPDF } from "../../utils/exportExpenses";
import { useTheme } from "@mui/material/styles";

export default function ExpensesTable({ selectedBranch = "" }) {
  const theme = useTheme();
  const isDarkMode = theme?.palette?.mode === "dark";
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedCategory]);

  const queryString = React.useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedCategory) params.push(`category=${selectedCategory}`);
    params.push(`page=${page}`);
    if (rowsPerPage !== -1) {
      params.push(`page_size=${rowsPerPage}`);
    } else {
      params.push(`page_size=10000`);
    }
    return params.join("&");
  }, [startDate, endDate, selectedBranch, selectedCategory, page, rowsPerPage]);

  const {
    data: expensesData,
    isLoading,
    isError,
  } = useFetchObjects(
    ["expenses", queryString, selectedCategory],
    `shop/expenses/?${queryString}`,
    token
  );

  // Fetch expense categories for filter dropdown
  const { data: categoriesData } = useFetchObjects(
    ["expense-categories"],
    "shop/expense-categories",
    token
  );

  const categories = categoriesData?.results || [];

  const expenses = expensesData?.results || [];
  const filtersActive = Boolean(startDate || endDate || selectedCategory);

  const filteredExpenses = React.useMemo(() => {
    let filtered = expenses;
    if (selectedCategory) {
      filtered = filtered.filter(
        (expense) =>
          String(expense.category) === String(selectedCategory) ||
          String(expense.category_details?.id) === String(selectedCategory)
      );
    }
    if (startDate || endDate) {
      filtered = filtered.filter((expense) => {
        if (!expense.payment_date) return false;
        const date = dayjs(expense.payment_date);
        if (startDate && date.isBefore(startDate, "day")) return false;
        if (endDate && date.isAfter(endDate, "day")) return false;
        return true;
      });
    }
    return filtered;
  }, [expenses, selectedCategory, startDate, endDate]);

  const totalCount = filtersActive
    ? filteredExpenses.length
    : expensesData?.count || 0;

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    return filteredExpenses.reduce((acc, expense) => {
      const amount = parseFloat(expense.amount) || 0;
      
      return {
        totalAmount: acc.totalAmount + amount
      };
    }, { totalAmount: 0 });
  }, [filteredExpenses]);

  const displayExpenses = React.useMemo(() => {
    if (rowsPerPage === -1) return filteredExpenses;
    const startIndex = (page - 1) * rowsPerPage;
    return filteredExpenses.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredExpenses, page, rowsPerPage]);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `expenses_${today}`;
    if (type === "pdf") exportExpensesToPDF(filteredExpenses, `${base}.pdf`);
    handleClose();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          bgcolor: isDarkMode ? "#111111" : "#ffffff",
        }}
      >
        <LinearProgress color="success" sx={{ width: "90%" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
          alignItems: "center",
          bgcolor: isDarkMode ? "#111111" : "#ffffff",
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Filters */}
      <Box
        sx={{
          flexGrow: 1,
          mb: 5,
          m: "15px",
          color: isDarkMode ? "#f3f4f6" : "#132238",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiInputBase-root": {
                          bgcolor: isDarkMode ? "#1b1b1b" : "#ffffff",
                          color: isDarkMode ? "#f3f4f6" : "inherit",
                        },
                        "& .MuiInputLabel-root": {
                          color: isDarkMode ? "#cbd5e1" : "inherit",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: isDarkMode ? "#4b4b4b" : "inherit",
                        },
                        "& .MuiSvgIcon-root": {
                          color: isDarkMode ? "#f3f4f6" : "inherit",
                        },
                      },
                    },
                  }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiInputBase-root": {
                          bgcolor: isDarkMode ? "#1b1b1b" : "#ffffff",
                          color: isDarkMode ? "#f3f4f6" : "inherit",
                        },
                        "& .MuiInputLabel-root": {
                          color: isDarkMode ? "#cbd5e1" : "inherit",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: isDarkMode ? "#4b4b4b" : "inherit",
                        },
                        "& .MuiSvgIcon-root": {
                          color: isDarkMode ? "#f3f4f6" : "inherit",
                        },
                      },
                    },
                  }}
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    displayEmpty
                    sx={{
                      bgcolor: isDarkMode ? "#1b1b1b" : "#ffffff",
                      color: isDarkMode ? "#f3f4f6" : "inherit",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: isDarkMode ? "#4b4b4b" : "inherit",
                      },
                      "& .MuiSvgIcon-root": {
                        color: isDarkMode ? "#f3f4f6" : "inherit",
                      },
                    }}
                  >
                    <MenuItem value="">{t("ReportIndex.all_categories")}</MenuItem>
                    {(categories || []).map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedCategory("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                    color: isDarkMode ? "#86ef7d" : "#355d31",
                    borderColor: isDarkMode ? "#355d31" : undefined,
                    "&:hover": {
                      borderColor: "#355d31",
                      bgcolor: isDarkMode
                        ? "rgba(53, 93, 49, 0.18)"
                        : "rgba(53, 93, 49, 0.08)",
                    },
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                    color: isDarkMode ? "#86ef7d" : "#355d31",
                    borderColor: isDarkMode ? "#355d31" : undefined,
                    "&:hover": {
                      borderColor: "#355d31",
                      bgcolor: isDarkMode
                        ? "rgba(53, 93, 49, 0.18)"
                        : "rgba(53, 93, 49, 0.08)",
                    },
                  }}
                >
                  {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ "aria-labelledby": "export-button" }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 2 }}>
        <Typography>{t("reports.rowsPerPage")}</Typography>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
            sx={{
              bgcolor: isDarkMode ? "#1b1b1b" : "#ffffff",
              color: isDarkMode ? "#f3f4f6" : "inherit",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDarkMode ? "#4b4b4b" : "inherit",
              },
              "& .MuiSvgIcon-root": {
                color: isDarkMode ? "#f3f4f6" : "inherit",
              },
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={-1}>{t("reports.all")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Expenses Table */}
      <TableContainer
        component={Paper}
        sx={{
          bgcolor: isDarkMode ? "#1a1a1a" : "#ffffff",
          border: isDarkMode ? "1px solid #353535" : "1px solid #e5e7eb",
          boxShadow: isDarkMode ? "0 10px 24px rgba(0,0,0,0.35)" : undefined,
        }}
      >
        <Table aria-label="expenses table">
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={7}
                align="center"
                sx={{
                  bgcolor: isDarkMode ? "#2a2a2a" : "inherit",
                  borderBottom: isDarkMode ? "1px solid #3a3a3a" : undefined,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ color: isDarkMode ? "#f9fafb" : "#132238" }}
                >
                  {t("ReportIndex.expenses_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow sx={{ backgroundColor: "#355d31" }}>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                {t("ReportIndex.expenses_table_id")}
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                {t("ReportIndex.expenses_table_category", "Expense Category")}
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                {t("ReportIndex.expenses_table_expenseBy")}
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                {t("ReportIndex.expenses_table_amount")}
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                {t("ReportIndex.expenses_table_paymentDate")}
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                {t("ReportIndex.expenses_table_notes")}
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                {t("ReportIndex.expenses_table_createdAt")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayExpenses.map((expense) => (
              <TableRow
                key={expense.id}
                sx={{
                  backgroundColor: isDarkMode ? "#262626" : "transparent",
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f8fbff",
                  },
                }}
              >
                <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "#132238" }}>{expense.id}</TableCell>
                <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "#132238" }}>{expense.category_details?.name || "-"}</TableCell>
                <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "#132238" }}>{expense.expense_by || "-"}</TableCell>
                <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "#132238" }}>
                  {expense.amount || "0"}{" "}
                  {expense.currency_details?.title || "-"}
                </TableCell>
                <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "#132238" }}>
                  {expense.payment_date
                    ? dayjs(expense.payment_date).format("YYYY-MM-DD")
                    : "-"}
                </TableCell>
                <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "#132238" }}>{expense.notes || "-"}</TableCell>
                <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "#132238" }}>
                  {expense.created_at
                    ? new Date(expense.created_at).toLocaleString()
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
            {/* Summary Row */}
            <TableRow
              sx={{
                backgroundColor: isDarkMode ? "#243f23" : "#f5f5f5",
                fontWeight: "bold",
              }}
            >
              <TableCell />
              <TableCell />
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: isDarkMode ? "#f9fafb" : "#132238",
                }}
              >
                {t("Summary")}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: isDarkMode ? "#f9fafb" : "#132238",
                }}
              >
                {summaryTotals.totalAmount.toFixed(2)}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {rowsPerPage !== -1 && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          padding="10px"
          sx={{ color: isDarkMode ? "#f3f4f6" : "#132238" }}
        >
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                color: isDarkMode ? "#f3f4f6" : "inherit",
                borderColor: isDarkMode ? "#355d31" : undefined,
              },
              "& .Mui-selected": {
                bgcolor: "#355d31 !important",
                color: "#fff !important",
              },
            }}
          />
        </Grid>
      )}
    </>
  );
}
